/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest(".js-product");
      this.productForm = this.section.querySelector(".js-product-form-main");
      this.optionSelectors = this.querySelectorAll(".option-selector");
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection =
        !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.addEventListener("change", this.handleVariantChange.bind(this));

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
        this.bindPdpAtcButton();
        this.initRestockCta();
        this.initInjectedPreorderObserver();
        this.updatePdpStockInline();
        this.updateRestockCta();
      });
    }

    initInjectedPreorderObserver() {
      const hasPdpSizeDrawer = !!this.section?.querySelector(".pdp-size-drawer");
      if (!hasPdpSizeDrawer || !this.productForm || this.injectedPreorderObserver) return;

      const selector =
        'input[name="properties[Shipping]"], .preorder-description-text, .restock-rocket-preorder-description, .preorder-detail-text';

      const matchesRelevantNode = (node) => {
        if (!node || node.nodeType !== Node.ELEMENT_NODE) return false;
        return node.matches?.(selector) || !!node.querySelector?.(selector);
      };

      this.injectedPreorderObserver = new MutationObserver((mutations) => {
        const shouldRefresh = mutations.some((mutation) => {
          if (mutation.type === "attributes") {
            return matchesRelevantNode(mutation.target);
          }

          if (mutation.type === "characterData") {
            return matchesRelevantNode(mutation.target?.parentElement);
          }

          return Array.from(mutation.addedNodes || []).some(matchesRelevantNode)
            || Array.from(mutation.removedNodes || []).some(matchesRelevantNode);
        });

        if (!shouldRefresh) return;

        window.clearTimeout(this.injectedPreorderSyncTimer);
        this.injectedPreorderSyncTimer = window.setTimeout(() => {
          this.updateSizeDrawer();
          this.syncInjectedPreorderUi();
        }, 90);
      });

      this.injectedPreorderObserver.observe(this.productForm, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ["value", "style", "hidden", "class"],
      });
    }

    initRestockCta() {
      this.addToCartRow =
        this.addToCartRow || this.productForm?.querySelector(".product-info__add-to-cart");
      this.addButtonWrap =
        this.addButtonWrap || this.productForm?.querySelector(".product-info__add-button");
      this.restockSlot =
        this.restockSlot || this.productForm?.querySelector(".pdp-restock-inline");
      if (!this.addToCartRow || !this.addButtonWrap || !this.restockSlot) return;

      this.normalizePdpPreorderUi();
      this.moveRestockButtonIntoSlot();

      if (this.restockObserver) return;
      this.restockObserver = new MutationObserver(() => {
        if (this.isSyncingRestockCta) return;
        this.normalizePdpPreorderUi();
        this.moveRestockButtonIntoSlot();
        this.updateRestockCta();
      });
      this.observeRestockCta();
    }

    normalizePdpPreorderUi() {
      const hasPdpSizeDrawer = !!this.section?.querySelector(".pdp-size-drawer");
      if (!hasPdpSizeDrawer || !this.productForm) return;

      this.addBtn = this.productForm.querySelector('[name="add"]');
      if (this.addBtn) {
        this.addBtn.type = "button";
        this.addBtn.hidden = false;
        this.addBtn.disabled = false;
        this.addBtn.classList.add("btn", "btn--pdp-atc", "w-full");
        this.addBtn.classList.remove("btn-not-selected");
        Array.from(this.addBtn.classList).forEach((className) => {
          if (className.startsWith("restock-rocket-preorder")) {
            this.addBtn.classList.remove(className);
          }
        });
      }

      this.productForm
        .querySelectorAll(".restock-rocket-preorder-description")
        .forEach((el) => {
          el.hidden = true;
          el.style.display = "none";
        });
    }

    observeRestockCta() {
      if (!this.restockObserver || !this.section) return;
      this.restockObserver.observe(this.section, { childList: true, subtree: true });
    }

    withRestockObserverPaused(callback) {
      this.isSyncingRestockCta = true;

      if (this.restockObserver) {
        this.restockObserver.disconnect();
      }

      try {
        callback();
      } finally {
        this.isSyncingRestockCta = false;
        this.observeRestockCta();
      }
    }

    moveRestockButtonIntoSlot() {
      if (!this.restockSlot) return;

      const restockButtons = this.getScopedRestockButtons();
      if (!restockButtons.length) return;

      const restockButton = restockButtons[restockButtons.length - 1];

      this.withRestockObserverPaused(() => {
        restockButtons.forEach((button) => {
          if (button !== restockButton) button.remove();
        });

        if (!this.restockSlot.contains(restockButton)) {
          this.restockSlot.replaceChildren(restockButton);
        }

        this.applyRestockButtonText(restockButton);
      });
    }

    applyRestockButtonText(restockButton) {
      const restockText = this.addButtonWrap?.dataset.restockText;
      if (!restockText || !restockButton) return;

      if (restockButton.tagName === "INPUT") {
        if (restockButton.value !== restockText) {
          restockButton.value = restockText;
        }
        return;
      }

      const labelEl = restockButton.querySelector("span");
      if (labelEl && labelEl.children.length === 0) {
        if (labelEl.textContent !== restockText) {
          labelEl.textContent = restockText;
        }
      } else if (restockButton.children.length === 0) {
        if (restockButton.textContent !== restockText) {
          restockButton.textContent = restockText;
        }
      } else {
        if (restockButton.getAttribute("aria-label") !== restockText) {
          restockButton.setAttribute("aria-label", restockText);
        }
      }
    }

    updateRestockCta() {
      this.addToCartRow =
        this.addToCartRow || this.productForm?.querySelector(".product-info__add-to-cart");
      this.addButtonWrap =
        this.addButtonWrap || this.productForm?.querySelector(".product-info__add-button");
      this.restockSlot =
        this.restockSlot || this.productForm?.querySelector(".pdp-restock-inline");
      this.addBtn = this.productForm?.querySelector('[name="add"]');
      if (!this.addToCartRow || !this.addButtonWrap || !this.restockSlot || !this.addBtn) return;

      this.moveRestockButtonIntoSlot();

      const hasSizeDrawer = !!this.section.querySelector(".pdp-size-drawer");
      if (hasSizeDrawer) {
        this.restockSlot.hidden = true;
        this.addBtn.hidden = false;
        this.addToCartRow.classList.remove("is-restock-mode");
        return;
      }

      const restockButton = this.restockSlot.querySelector(".restock-rocket-button-product");
      const shouldShowRestock = !!(this.variant && this.variant.available === false && restockButton);

      this.restockSlot.hidden = !shouldShowRestock;
      this.addBtn.hidden = shouldShowRestock;
      this.addToCartRow.classList.toggle("is-restock-mode", shouldShowRestock);

      if (shouldShowRestock) {
        this.clearProductFormError();
      }
    }

    clearProductFormError() {
      const errorEl = this.productForm?.querySelector(".js-form-error");
      if (!errorEl) return;

      errorEl.hidden = true;
      errorEl.innerHTML = "";
    }

    bindPdpAtcButton() {
      if (!this.productForm) return;

      const hasPdpSizeDrawer = !!this.section.querySelector(".pdp-size-drawer");
      if (hasPdpSizeDrawer) {
        this.normalizePdpPreorderUi();
      }

      this.addBtn = this.productForm.querySelector('[name="add"]');
      if (!this.addBtn || (!hasPdpSizeDrawer && !this.addBtn.classList.contains("btn--pdp-atc"))) return;
      if (this.addBtn.dataset.pdpAtcBound === "true") return;

      this.addBtn.dataset.pdpAtcBound = "true";
      this.addBtn.dataset.pdpAtcInitialDisabled = this.addBtn.disabled ? "true" : "false";
      this.addBtn.addEventListener("click", this.handlePdpAtcClick.bind(this));
      if (!this.pdpAtcCartListenerBound) {
        this.pdpAtcCartListenerBound = true;
        this.section.addEventListener("on:cart:add", () => {
          window.setTimeout(() => {
            this.bindPdpAtcButton();
            this.updateAddToCartButton();
          }, 80);
        });
      }

      this.bindSizeDrawer();
    }

    bindSizeDrawer() {
      this.sizeDrawer = this.sizeDrawer || this.section.querySelector(".pdp-size-drawer");
      if (!this.sizeDrawer) return;
      if (this.sizeDrawer.dataset.bound === "true") return;
      this.sizeDrawer.dataset.bound = "true";

      this.sizeSelectorIndex = this.getSizeSelectorIndex();

      this.sizeDrawer.querySelectorAll("[data-size-drawer-close]").forEach((el) => {
        el.addEventListener("click", () => this.closeSizeDrawer());
      });

      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.sizeDrawer.classList.contains("is-open")) {
          this.closeSizeDrawer();
        }
      });

      this.sizeDrawer.querySelectorAll("[data-size-value]").forEach((btn) => {
        btn.addEventListener("click", (evt) => {
          evt.preventDefault();
          evt.stopPropagation();
          evt.stopImmediatePropagation?.();

          if (evt.target.closest("[data-size-notify]")) {
            this.handleSizeNotifyClick(btn);
            return;
          }

          if (btn.disabled || btn.getAttribute("aria-disabled") === "true") return;
          const value = btn.getAttribute("data-size-value");
          this.selectSizeAndAdd(value);
        });
      });

      // Ensure initial button label/state reflects drawer-based selection.
      this.updateAddToCartButton();
    }

    getSizeSelectorIndex() {
      const sizeOptionPattern = /(size|talla|taille|taglia|pointure|misura|tama(?:ñ|Ã±)o)/i;
      const idx = Array.from(this.optionSelectors).findIndex(
        (s) => s.dataset.optionRole === "size"
          || sizeOptionPattern.test(s.dataset.optionName || "")
      );
      return idx >= 0 ? idx : null;
    }

    handlePdpAtcClick(evt) {
      if (!this.productForm) return;

      // Refresh state just-in-time
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      const variantAvailable = this.variant && this.variant.available;

      evt.preventDefault();

      // If we have a size drawer, always use it (even if a variant is preselected).
      if (this.sizeDrawer && this.sizeSelectorIndex !== null) {
        this.openSizeDrawer();
        return;
      } else {
        // Fallback: Require selecting a variant before ATC.
        if (variantAvailable) {
          this.submitProductForm();
        } else {
          this.openFirstUnselectedSelector();
        }
      }
    }

    openFirstUnselectedSelector() {
      const idx = this.selectedOptions.findIndex((o) => o === null);
      const selector = idx >= 0 ? this.optionSelectors[idx] : this.optionSelectors[0];
      if (!selector) return;

      selector.scrollIntoView({ block: "center", behavior: "smooth" });
      selector.classList.add("is-attn");
      window.setTimeout(() => selector.classList.remove("is-attn"), 1500);

      const customSelect = selector.querySelector("custom-select");
      if (customSelect && typeof customSelect.showListbox === "function") {
        customSelect.showListbox();
      } else {
        const firstInput = selector.querySelector("input, button, [tabindex]");
        if (firstInput) firstInput.focus({ preventScroll: true });
      }
    }

    openSizeDrawer() {
      if (!this.sizeDrawer) return;
      this.updateSizeDrawer();
      this.setPageScrollLocked(true);
      this.sizeDrawer.hidden = false;
      this.sizeDrawer.classList.add("is-open");
      const panel = this.sizeDrawer.querySelector(".pdp-size-drawer__panel");
      if (panel) panel.focus?.({ preventScroll: true });
    }

    closeSizeDrawer() {
      if (!this.sizeDrawer) return;
      this.setPageScrollLocked(false);
      this.sizeDrawer.classList.remove("is-open");
      window.setTimeout(() => {
        this.sizeDrawer.hidden = true;
      }, 230);
    }

    selectSizeAndAdd(sizeValue) {
      if (this.sizeSelectorIndex === null) return;
      this.setOptionValueByIndex(this.sizeSelectorIndex, sizeValue);

      window.setTimeout(() => {
        this.selectedOptions = this.getSelectedOptions();
        this.variant = this.getSelectedVariant();
        if (this.variant && this.variant.available) {
          this.updateVariantInput();
          this.updateAddToCartButton();
          this.suppressUnexpectedPreorderModal();
          this.submitProductForm();
          this.closeSizeDrawer();
          window.setTimeout(() => {
            this.updateAddToCartButton();
          }, 120);
        }
      }, 30);
    }

    submitProductForm() {
      if (!this.productForm) return;

      const idInput = this.productForm.querySelector('[name="id"]');
      if (idInput) idInput.disabled = false;

      const productFormEl = this.productForm.closest("product-form");
      if (productFormEl && typeof productFormEl.handleSubmit === "function") {
        productFormEl.handleSubmit({
          preventDefault() {},
          target: this.productForm,
        });
        return;
      }

      this.productForm.dispatchEvent(
        new Event("submit", { bubbles: true, cancelable: true })
      );
    }

    suppressUnexpectedPreorderModal() {
      const markers = [
        "No units available for preorder",
        "Exclusive pre-order. Now in production.",
        "Estimated shipping around",
      ];

      const matchesPreorderModal = (el) => {
        const text = el?.textContent?.replace(/\s+/g, " ").trim() || "";
        return markers.some((marker) => text.includes(marker));
      };

      const hideElement = (el) => {
        if (!el || el.dataset.preorderSuppressed === "true") return;
        el.dataset.preorderSuppressed = "true";
        el.style.setProperty("display", "none", "important");
        el.style.setProperty("visibility", "hidden", "important");
        el.style.setProperty("opacity", "0", "important");
        el.setAttribute("hidden", "hidden");
        el.setAttribute("aria-hidden", "true");
      };

      const closeCandidates = () => {
        const candidates = Array.from(
          document.querySelectorAll('[role="dialog"], [aria-modal="true"], .ReactModalPortal, [class*="modal"], [class*="popup"]')
        );

        candidates.forEach((candidate) => {
          if (!matchesPreorderModal(candidate)) return;

          const closeButton = candidate.querySelector(
            'button[aria-label*="close" i], button[aria-label*="cerrar" i], button[class*="close" i]'
          );

          if (closeButton && typeof closeButton.click === "function") {
            closeButton.click();
          }

          hideElement(candidate);

          let parent = candidate.parentElement;
          for (let depth = 0; parent && depth < 3; depth += 1) {
            if (matchesPreorderModal(parent) || parent.childElementCount <= 2) {
              hideElement(parent);
            }
            parent = parent.parentElement;
          }
        });
      };

      closeCandidates();

      const observer = new MutationObserver(() => {
        closeCandidates();
      });

      observer.observe(document.body, { childList: true, subtree: true });
      window.setTimeout(() => observer.disconnect(), 4000);
    }

    async addVariantToCartSilently() {
      if (!this.productForm || !this.variant?.id) return false;

      this.suppressUnexpectedPreorderModal();

      const quantityInput = this.productForm.querySelector('[name="quantity"]');
      const quantity = Math.max(parseInt(quantityInput?.value || "1", 10) || 1, 1);
      const cartDrawer = document.querySelector("cart-drawer");
      let sections = "cart-icon-bubble";

      if (cartDrawer) {
        sections += `,${cartDrawer.closest(".shopify-section").id.replace("shopify-section-", "")}`;
      }

      const formData = new FormData();
      formData.append("id", `${this.variant.id}`);
      formData.append("quantity", `${quantity}`);
      formData.append("sections_url", window.location.pathname);
      formData.append("sections", sections);

      const response = await fetch(theme.routes.cartAdd, {
        method: "POST",
        headers: {
          Accept: "application/javascript",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formData,
      });

      const data = await response.json();
      const error = typeof data?.description === "string" ? data.description : data?.message;

      if (!response.ok || data?.status) {
        this.productForm.closest("product-form")?.setErrorMsgState(
          error || theme?.strings?.noStock || "Error"
        );
        return false;
      }

      const cartIconBubble = document.getElementById("cart-icon-bubble");
      if (cartIconBubble && data.sections?.["cart-icon-bubble"]) {
        cartIconBubble.innerHTML = data.sections["cart-icon-bubble"];
      }

      const productFormEl = this.productForm.closest("product-form");
      if (productFormEl) {
        productFormEl.dispatchEvent(
          new CustomEvent("on:cart:add", {
            bubbles: true,
            detail: {
              cart: null,
              variantId: data.variant_id,
            },
          })
        );
      }

      return true;
    }

    setPageScrollLocked(isLocked) {
      document.documentElement.classList.toggle("pdp-size-drawer-open", isLocked);
      document.body.classList.toggle("pdp-size-drawer-open", isLocked);
    }

    /**
     * Handles clicks on the inline notify action for sold out sizes.
     * It keeps the sold out variant selected, then reuses the injected restock trigger.
     * @param {Element} btn - Size option button.
     */
    handleSizeNotifyClick(btn) {
      const sizeValue = btn?.getAttribute("data-size-value");
      if (!sizeValue || this.sizeSelectorIndex === null) return;

      this.setOptionValueByIndex(this.sizeSelectorIndex, sizeValue);

      window.setTimeout(() => {
        this.selectedOptions = this.getSelectedOptions();
        this.variant = this.getSelectedVariant();
        this.clearProductFormError();
        if (this.restockSlot) this.restockSlot.hidden = true;
        if (this.addBtn) this.addBtn.hidden = false;
        if (this.addToCartRow) {
          this.addToCartRow.classList.remove("is-restock-mode");
        }
        this.updateSizeDrawer();

        if (this.openRestockRocketModal()) {
          return;
        }

        if (this.openExistingRestockModal()) {
          return;
        }

        this.triggerExistingRestockButton(0);
      }, 80);
    }

    openRestockRocketModal() {
      if (!this.variant?.id || !window._RestockRocket?.openModal) return false;

      this.closeSizeDrawer();
      this.updateVariantInput();

      window.setTimeout(() => {
        window._RestockRocket.openModal(this.data?.product, `${this.variant.id}`);
      }, 60);

      return true;
    }

    openExistingRestockModal() {
      const wrapper = document.querySelector("#restock-rocket .restock-rocket-wrapper");
      const container = wrapper?.querySelector("#formContainer");
      if (!wrapper || !container) return false;

      const select = container.querySelector("#form-Select");
      if (select && this.variant?.id) {
        select.value = `${this.variant.id}`;
        select.dispatchEvent(new Event("change", { bubbles: true }));
        select.dispatchEvent(new Event("input", { bubbles: true }));
      }

      wrapper.hidden = false;
      container.hidden = false;
      wrapper.style.display = "flex";
      wrapper.style.visibility = "visible";
      wrapper.style.opacity = "1";
      wrapper.style.pointerEvents = "auto";
      container.style.display = "";
      container.style.visibility = "visible";
      container.style.opacity = "1";
      container.removeAttribute("aria-hidden");

      const emailInput = container.querySelector("#form-Email");
      window.setTimeout(() => {
        container.focus?.({ preventScroll: true });
        emailInput?.focus?.({ preventScroll: true });
      }, 60);

      return true;
    }

    /**
     * Reuses the existing Restock Rocket button, if present.
     * Retries briefly to allow the app to react to the variant change.
     * @param {number} attempt - Retry count.
     * @returns {boolean} Whether a click was dispatched synchronously.
     */
    triggerExistingRestockButton(attempt = 0) {
      const restockButton = this.getRestockTriggerButton();
      if (restockButton) {
        if (this.variant) {
          this.updateVariantInput();
        }

        this.clearProductFormError();
        if (this.restockSlot) this.restockSlot.hidden = true;
        if (this.addBtn) this.addBtn.hidden = false;
        if (this.addToCartRow) {
          this.addToCartRow.classList.remove("is-restock-mode");
        }

        this.closeSizeDrawer();
        const restockSlotWasHidden = this.restockSlot?.hidden;
        const restockButtonHidden = restockButton.hidden;
        const previousDisplay = restockButton.style.display;
        const previousVisibility = restockButton.style.visibility;
        const previousPointerEvents = restockButton.style.pointerEvents;
        const isInlineRestockButton =
          !!this.restockSlot && this.restockSlot.contains(restockButton);

        if (isInlineRestockButton && this.restockSlot) this.restockSlot.hidden = false;
        restockButton.hidden = false;
        restockButton.style.display = "";
        restockButton.style.visibility = "visible";
        restockButton.style.pointerEvents = "auto";

        window.setTimeout(() => {
          ["pointerdown", "mousedown", "mouseup", "click"].forEach((eventName) => {
            restockButton.dispatchEvent(
              new MouseEvent(eventName, {
                bubbles: true,
                cancelable: true,
                view: window,
              })
            );
          });

          if (typeof restockButton.click === "function") {
            restockButton.click();
          }

          window.setTimeout(() => {
            restockButton.hidden = restockButtonHidden;
            restockButton.style.display = previousDisplay;
            restockButton.style.visibility = previousVisibility;
            restockButton.style.pointerEvents = previousPointerEvents;

            if (isInlineRestockButton && this.restockSlot && restockSlotWasHidden) {
              this.restockSlot.hidden = true;
            }
          }, 400);
        }, 120);
        return true;
      }

      if (attempt >= 4) return false;

      window.setTimeout(() => {
        this.triggerExistingRestockButton(attempt + 1);
      }, 120);

      return false;
    }

    getScopedRestockButtons() {
      const allButtons = Array.from(
        document.querySelectorAll(".restock-rocket-button-product")
      );
      if (!allButtons.length) return [];

      const sectionButtons = allButtons.filter((button) => {
        const parentProduct = button.closest(".js-product");
        return parentProduct === this.section || this.section.contains(button);
      });

      if (sectionButtons.length) return sectionButtons;

      const globalRoot = document.querySelector("#restock-rocket");
      const globalButtons = globalRoot
        ? Array.from(globalRoot.querySelectorAll(".restock-rocket-button-product"))
        : [];

      return globalButtons.length ? globalButtons : allButtons;
    }

    /**
     * Gets the existing restock trigger injected by the app.
     * @returns {?Element}
     */
    getRestockTriggerButton() {
      const scopedButtons = this.getScopedRestockButtons();
      const inlineButton =
        this.restockSlot?.querySelector(".restock-rocket-button-product");
      const hasSizeDrawer = !!this.section.querySelector(".pdp-size-drawer");
      if (!hasSizeDrawer) {
        return inlineButton
          || scopedButtons[scopedButtons.length - 1]
          || null;
      }

      return inlineButton
        || scopedButtons[scopedButtons.length - 1]
        || this.section.querySelector(".restock-rocket-button-product")
        || this.productForm?.querySelector(".restock-rocket-button-product")
        || this.restockSlot?.querySelector(".restock-rocket-button-product")
        || null;
    }

    getInjectedPreorderDescription() {
      const shippingInput = this.productForm?.querySelector('input[name="properties[Shipping]"]');
      const shippingValue = shippingInput?.value?.trim();
      if (shippingValue) return shippingValue;

      const detailTextEl = this.section.querySelector(
        ".restock-rocket-preorder-description .preorder-detail-text"
      );
      const detailText = detailTextEl?.textContent?.replace(/\s+/g, " ").trim() || "";
      if (detailText) {
        return detailText.replace(/^Shipping:\s*/i, "").trim();
      }

      const legacyTextEl = this.section.querySelector(".preorder-description-text");
      const legacyText = legacyTextEl?.textContent?.replace(/\s+/g, " ").trim() || "";
      if (!legacyText) return "";

      return legacyText
        .replace(/^Shipping:\s*/i, "")
        .replace(/^Env(?:i|í)o:\s*/i, "")
        .trim();
    }

    syncInjectedPreorderUi() {
      this.section.querySelectorAll(".preorder-description-text").forEach((el) => {
        el.style.display = "none";
        el.hidden = true;
      });

      this.section.querySelectorAll(".restock-rocket-preorder-description").forEach((el) => {
        el.style.display = "none";
        el.hidden = true;
      });
    }

    getScopedVariantsExcludingSize() {
      const variants = this.data?.product?.variants || [];
      const sizeSelectorIndex =
        this.sizeSelectorIndex ?? this.getSizeSelectorIndex();

      if (sizeSelectorIndex === null) return variants;

      const selected = this.getSelectedOptions();
      return variants.filter((variant) => {
        for (let i = 0; i < selected.length; i += 1) {
          if (i === sizeSelectorIndex) continue;
          if (selected[i] === null) continue;
          if (variant.options[i] !== selected[i]) return false;
        }
        return true;
      });
    }

    getDrawerPreorderCandidate() {
      const scopedVariants = this.getScopedVariantsExcludingSize();
      const availableVariants = scopedVariants.filter((variant) => variant.available);
      const inStockVariants = availableVariants.filter((variant) => !this.isVariantPreorder(variant));
      const preorderVariants = availableVariants.filter((variant) => this.isVariantPreorder(variant));

      if (!preorderVariants.length || inStockVariants.length) return null;
      return preorderVariants[0];
    }


    setOptionValueByIndex(optionIndex, value) {
      const selector = this.optionSelectors[optionIndex];
      if (!selector) return;

      if (selector.dataset.selectorType === "dropdown") {
        const customSelect = selector.querySelector("custom-select");
        const native = customSelect
          ? document.getElementById(`${customSelect.id}-native`)
          : selector.querySelector("select");
        if (native) {
          native.value = value;
          native.dispatchEvent(new Event("change", { bubbles: true }));
        }
        return;
      }

      const inputs = selector.querySelectorAll(".js-option");
      const input = Array.from(inputs).find((el) => el.value === value);
      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    updateSizeDrawer() {
      if (!this.sizeDrawer || this.sizeSelectorIndex === null) return;

      const inStockText = this.sizeDrawer.dataset.inStockText || "In stock";
      const preorderText = this.sizeDrawer.dataset.preorderText || "Pre-order";
      const soldOutText = this.sizeDrawer.dataset.soldOutText || "Sold out";
      const injectedPreorderShipping = this.getInjectedPreorderDescription();

      this.selectedOptions = this.getSelectedOptions();
      const selected = this.selectedOptions;

      const matchesForSize = (variant, sizeValue) => {
        if (variant.options[this.sizeSelectorIndex] !== sizeValue) return false;
        for (let i = 0; i < selected.length; i += 1) {
          if (i === this.sizeSelectorIndex) continue;
          if (selected[i] === null) continue;
          if (variant.options[i] !== selected[i]) return false;
        }
        return true;
      };

      this.sizeDrawer.querySelectorAll("[data-size-value]").forEach((btn) => {
        const sizeValue = btn.getAttribute("data-size-value");
        const candidates = this.data.product.variants.filter((v) =>
          matchesForSize(v, sizeValue)
        );
        const anyAvailable = candidates.some((v) => v.available);
        const isSoldOut = candidates.length > 0 && !anyAvailable;
        const displayVariant =
          candidates.find((v) => v.available) || candidates[0] || null;
        const isPreorder = this.isVariantPreorder(displayVariant);
        const formatted = displayVariant
          ? this.data?.formatted?.[displayVariant.id]
          : null;

        btn.disabled = candidates.length === 0;
        btn.setAttribute(
          "aria-disabled",
          btn.disabled || isSoldOut ? "true" : "false"
        );
        btn.dataset.sizeState = isSoldOut
          ? "sold_out"
          : (isPreorder ? "preorder" : (anyAvailable ? "available" : "unavailable"));
        btn.dataset.sizeVariantId = displayVariant?.id
          ? `${displayVariant.id}`
          : "";

        const statusEl = btn.querySelector("[data-size-status]");
        if (statusEl) {
          statusEl.textContent = isSoldOut
            ? soldOutText
            : (isPreorder ? preorderText : inStockText);
        }

        const statusDetailEl = btn.querySelector("[data-size-status-detail]");
        if (statusDetailEl) {
          statusDetailEl.hidden = true;
          statusDetailEl.textContent = "";
        }

        const shippingEl = btn.querySelector("[data-size-shipping]");
        if (shippingEl) {
          const preorderShippingText = isPreorder ? injectedPreorderShipping : "";
          const showPreorderShipping = isPreorder && !!preorderShippingText;

          btn.dataset.sizeHasShipping = showPreorderShipping ? "true" : "false";
          shippingEl.hidden = !showPreorderShipping;
          shippingEl.replaceChildren();

          if (showPreorderShipping) {
            const dateEl = document.createElement("span");
            dateEl.className = "pdp-size-drawer__shipping-date";
            dateEl.textContent = preorderShippingText;

            shippingEl.append(dateEl);
          }
        } else {
          btn.dataset.sizeHasShipping = "false";
        }

        const notifyEl = btn.querySelector("[data-size-notify]");
        if (notifyEl) notifyEl.hidden = !isSoldOut;

        const pricingEl = btn.querySelector("[data-size-pricing]");
        const priceNowEl = btn.querySelector("[data-size-price-now]");
        const priceWasEl = btn.querySelector("[data-size-price-was]");

        if (pricingEl && priceNowEl) {
          const hasPrice = !!formatted?.price;
          const showPricing = hasPrice && !isSoldOut;
          pricingEl.hidden = !showPricing;
          priceNowEl.innerHTML = showPricing ? formatted.price : "";

          if (priceWasEl) {
            const hasComparePrice = !!formatted?.compareAtPrice && !isSoldOut;
            priceWasEl.hidden = !hasComparePrice;
            priceWasEl.innerHTML = hasComparePrice ? formatted.compareAtPrice : "";
          }
        }
      });
    }

    isVariantPreorder(variant = this.variant) {
      if (!variant || !variant.available || variant.inventory_management !== "shopify") {
        return false;
      }

      return this.data?.formatted?.[variant.id]?.inventory === "none";
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection =
        !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.clearProductFormError();

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updateSizeDrawer();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updatePdpStockInline();
      this.updateRestockCta();
      this.updateMetafieldVisibility();
      VariantPicker.updateLabelText(evt);

      window.clearTimeout(this.preorderUiSyncTimer);
      this.preorderUiSyncTimer = window.setTimeout(() => {
        this.bindPdpAtcButton();
        this.updateAddToCartButton();
        this.syncInjectedPreorderUi();
      }, 180);

      window.clearTimeout(this.pdpAtcResyncTimer);
      this.pdpAtcResyncTimer = window.setTimeout(() => {
        this.normalizePdpPreorderUi();
        this.bindPdpAtcButton();
        this.updateAddToCartButton();
        this.updateSizeDrawer();
        this.updateRestockCta();
      }, 420);

      window.clearTimeout(this.stoqShippingResyncTimer);
      this.stoqShippingResyncTimer = window.setTimeout(() => {
        this.updateSizeDrawer();
      }, 900);

      if (!this.preSelection) {
        this.dispatchEvent(
          new CustomEvent("on:variant:change", {
            bubbles: true,
            detail: {
              form: this.productForm,
              variant: this.variant,
              product: this.data.product,
            },
          })
        );
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll("[data-variant-metafield]").forEach((elem) => {
        elem.classList.toggle(
          "hidden",
          elem.dataset.variantMetafield !== this.variant?.id.toString()
        );
      });
    }

    /**
     * Determines the compact PDP stock state under the title.
     */
    getPdpStockInlineState() {
      const variants = this.data?.product?.variants || [];
      if (!variants.length) return "sold_out";

      const sizeSelectorIndex =
        this.sizeSelectorIndex ?? this.getSizeSelectorIndex();
      if (this.sizeSelectorIndex === undefined) {
        this.sizeSelectorIndex = sizeSelectorIndex;
      }

      if (sizeSelectorIndex === null) {
        return variants.some((variant) => variant.available)
          ? "in_stock"
          : "sold_out";
      }

      const selected = this.getSelectedOptions();
      const scopedVariants = variants.filter((variant) => {
        for (let i = 0; i < selected.length; i += 1) {
          if (i === sizeSelectorIndex) continue;
          if (selected[i] === null) continue;
          if (variant.options[i] !== selected[i]) return false;
        }
        return true;
      });

      const variantsToCheck = scopedVariants.length ? scopedVariants : variants;
      const sizeAvailability = new Map();

      variantsToCheck.forEach((variant) => {
        const sizeValue = variant.options[sizeSelectorIndex];
        if (!sizeValue) return;

        const isAvailable = sizeAvailability.get(sizeValue) || false;
        sizeAvailability.set(sizeValue, isAvailable || variant.available);
      });

      if (!sizeAvailability.size) {
        return variantsToCheck.some((variant) => variant.available)
          ? "in_stock"
          : "sold_out";
      }

      let missingSizes = 0;
      sizeAvailability.forEach((isAvailable) => {
        if (!isAvailable) missingSizes += 1;
      });

      if (missingSizes >= sizeAvailability.size) return "sold_out";
      if (missingSizes >= 4) return "almost_sold_out";
      return "in_stock";
    }

    /**
     * Updates the compact PDP stock line under the title (if present).
     */
    updatePdpStockInline() {
      const el = this.section.querySelector(".pdp-stock-inline");
      if (!el) return;

      const inStockText = el.dataset.inStockText || theme.strings.inStock;
      const almostSoldOutText =
        el.dataset.almostSoldOutText
        || theme.strings.almostSoldOut
        || theme.strings.veryLowStock;
      const outOfStockText = el.dataset.outOfStockText || theme.strings.noStock;
      const stockState = this.getPdpStockInlineState();

      if (stockState === "sold_out") {
        el.textContent = outOfStockText;
      } else if (stockState === "almost_sold_out") {
        el.textContent = almostSoldOutText;
      } else {
        el.textContent = inStockText;
      }

      el.dataset.stockState = stockState;
      el.hidden = false;
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    setDrawerCtaState() {
      if (!this.addBtn) return;

      const drawerCtaText =
        this.addBtn.dataset.selectSizeText || theme.strings.noSelectedVariant;

      this.addBtn.type = "button";
      this.addBtn.hidden = false;
      this.addBtn.disabled = false;
      this.addBtn.textContent = drawerCtaText;
      this.addBtn.setAttribute("aria-label", drawerCtaText);
      this.addBtn.setAttribute("data-atc-state", "select");
    }

    updateAddToCartButton() {
      this.productForm = this.section.querySelector(".js-product-form-main");
      if (!this.productForm) return;

      this.addBtn = this.productForm.querySelector('[name="add"]');
      if (!this.addBtn) return;
      this.preorderNote =
        this.preorderNote || this.section.querySelector("[data-preorder-note]");
      const variantAvailable = this.variant && this.variant.available;
      const hasPdpSizeDrawer = !!this.section.querySelector(".pdp-size-drawer");

      if (hasPdpSizeDrawer) {
        this.normalizePdpPreorderUi();
        this.setDrawerCtaState();

        if (this.preorderNote) {
          this.preorderNote.textContent = "";
          this.preorderNote.hidden = true;
        }

        this.syncInjectedPreorderUi();
        return;
      }

      const isPdpAtc = hasPdpSizeDrawer || this.addBtn.classList.contains("btn--pdp-atc");
      if (isPdpAtc) {
        // Keep clickable so it can open the size drawer, even on sold out/preorder colors.
        const lockedDisabled = this.addBtn.dataset.pdpAtcInitialDisabled === "true";
        this.addBtn.disabled = hasPdpSizeDrawer ? false : lockedDisabled;
      } else {
        this.addBtn.disabled = !variantAvailable || this.preSelection;
      }

      if (isPdpAtc && hasPdpSizeDrawer) {
        this.addBtn.hidden = false;
        this.addBtn.textContent =
          this.addBtn.dataset.selectSizeText || theme.strings.noSelectedVariant;

        if (this.preorderNote) {
          this.preorderNote.textContent = "";
          this.preorderNote.hidden = true;
        }
      } else if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {
        const unavailableStr = this.variant
          ? theme.strings.noStock
          : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;

        if (this.preorderNote) {
          this.preorderNote.textContent = "";
          this.preorderNote.hidden = true;
        }
      }

      if (isPdpAtc) {
        this.addBtn.setAttribute(
          "data-atc-state",
          this.section.querySelector(".pdp-size-drawer")
            ? "select"
            : (variantAvailable ? "ready" : "select")
        );
      }

      this.syncInjectedPreorderUi();
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists
        ? theme.strings.noStock
        : theme.strings.noVariant;
      el.classList.toggle("is-unavailable", !available);
      el.classList.toggle("is-nonexistent", !exists);

      if (optionEl.classList.contains("custom-select__option")) {
        const em = el.querySelector("em");

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute("title");
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === "false") return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === "selection") {
        // Flag all options as unavailable
        this.querySelectorAll(".js-option").forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (
                option === currVariant.options[optionIndex] &&
                optionIndex !== selectorIndex
              ) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll(".js-option");
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === "dropdown") {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(
                  optionEl,
                  true,
                  variant.available
                );
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll(
            '.js-option:not([data-value=""])'
          );
          options.forEach((option) => {
            const optionValue =
              selector.dataset.selectorType === "dropdown"
                ? option.dataset.value
                : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (
                  v.options[i] === this.selectedOptions[i] ||
                  this.selectedOptions[i] === null
                ) {
                  matches += 1;
                }
              }
              if (
                v.options[selectorIndex] === optionValue &&
                matches === selectorIndex
              ) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(
              option,
              variantsExist,
              variantsAvailable
            );
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder =
        this.backorder || this.section.querySelector(".backorder");
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === "none") {
          const backorderProdEl = this.backorder.querySelector(
            ".backorder__product"
          );
          const prodTitleEl = this.section.querySelector(".product-title");
          const variantTitle = this.variant.title.includes("Default")
            ? ""
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest(".option-selector");
      if (selector.dataset.selectorType === "dropdown") return;

      const colorText = selector.querySelector(".js-color-text");
      if (!colorText) return;

      colorText.textContent =
        evt.target.nextElementSibling.querySelector(".js-value").textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches("quick-add-drawer")) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery =
          this.mediaGallery || this.section.querySelector("media-gallery");
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability ||
        this.section.querySelector("pickup-availability");
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute("available");
        this.pickUpAvailability.innerHTML = "";
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price =
        this.price ||
        this.section.querySelector(".product-info__price > .price");
      if (!this.price) return;

      let { variant } = this;
      if (this.preSelection) {
        variant = this.data.product.variants[0];
        for (let i = 1; i < this.data.product.variants.length; i += 1) {
          if (this.data.product.variants[i].price < variant.price)
            variant = this.data.product.variants[i];
        }
      }

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector(".price__current");
        const priceWasEl = this.price.querySelector(".price__was");
        const unitPriceEl = this.price.querySelector(".unit-price");

        // Update current price and original price if on sale.
        priceCurrentEl.innerHTML = this.data.formatted[this.variant.id].price;
        if (priceWasEl)
          priceWasEl.innerHTML =
            this.data.formatted[this.variant.id].compareAtPrice || "";

        // Update unit price, if specified.
        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector(".unit-price__price");
          const unitEl = this.price.querySelector(".unit-price__unit");
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          valueEl.innerHTML = this.data.formatted[this.variant.id].unitPrice;
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
        }

        unitPriceEl.hidden = !this.variant.unit_price_measurement;
        this.price.classList.toggle(
          "price--on-sale",
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle(
          "price--sold-out",
          !this.variant.available && !this.preSelection
        );
      }

      this.price.querySelector(".price__default").hidden =
        !this.variant && !this.preSelection;
      this.price.querySelector(".price__no-variant").hidden =
        this.variant || this.preSelection;
      const from = this.price.querySelector(".price__from");
      if (from) {
        from.hidden = !this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights =
        this.weights || this.section.querySelectorAll(".product-info__weight");
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable
          ? this.data.formatted[this.variant.id].weight
          : "";
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes =
        this.barcodes ||
        this.section.querySelectorAll(".product-info__barcode-value");
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : "";
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector(".product-sku__value");
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : "";
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== "change" || this.dataset.updateUrl === "false")
        return;
      window.history.replaceState(
        {},
        "",
        `${this.dataset.url}?variant=${this.variant.id}`
      );
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms ||
        this.section.querySelectorAll(
          ".js-product-form-main, .js-instalments-form"
        );

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === "dropdown") {
          const selectedText = selector
            .querySelector(".custom-select__btn")
            .textContent.trim();
          selectedOptions.push(
            selectedText === this.dataset.placeholderText ? null : selectedText
          );
        } else {
          const selected = selector.querySelector("input:checked");
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find((v) =>
        v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define("variant-picker", VariantPicker);
}
